<template>
    <div class="at-page">
        <div class="at-header">
            <div>
                <h2>{{ $t('apiTokens.title') }}</h2>
                <p class="at-subtitle">{{ $t('apiTokens.subtitle') }}</p>
            </div>
            <button class="at-btn-new" @click="showCreate = true">
                <i class="tim-icons icon-simple-add"></i> {{ $t('apiTokens.newToken') }}
            </button>
        </div>

        <!-- Docs link -->
        <div class="at-docs card">
            <i class="tim-icons icon-paper"></i>
            <span>{{ $t('apiTokens.docsLabel') }}: </span>
            <a :href="docsUrl" target="_blank">{{ docsUrl }}</a>
        </div>

        <!-- Token list -->
        <div class="at-table-wrap card">
            <table class="at-table">
                <thead>
                    <tr>
                        <th>{{ $t('apiTokens.table.name') }}</th>
                        <th>{{ $t('apiTokens.table.token') }}</th>
                        <th>{{ $t('apiTokens.table.status') }}</th>
                        <th>{{ $t('apiTokens.table.lastUsed') }}</th>
                        <th>{{ $t('apiTokens.table.created') }}</th>
                        <th>{{ $t('apiTokens.table.createdBy') }}</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="t in tokens" :key="t.id" :class="{ 'at-inactive': !t.active }">
                        <td><strong>{{ t.name }}</strong></td>
                        <td class="at-token-cell">
                            <code>ft_•••••••••••</code>
                        </td>
                        <td>
                            <span :class="['at-status', t.active ? 'at-st-active' : 'at-st-revoked']">
                                {{ t.active ? $t('apiTokens.table.active') : $t('apiTokens.table.revoked') }}
                            </span>
                        </td>
                        <td>{{ t.lastUsed ? formatDate(t.lastUsed) : $t('apiTokens.table.never') }}</td>
                        <td>{{ formatDate(t.timeStamp) }}</td>
                        <td>{{ t.createdBy ? t.createdBy.name || t.createdBy.username : '-' }}</td>
                        <td class="at-actions">
                            <button v-if="t.active" class="at-btn-revoke" @click="revokeToken(t)" :title="$t('common.cancel')">
                                <i class="tim-icons icon-button-power"></i>
                            </button>
                            <button class="at-btn-delete" @click="deleteToken(t)" :title="$t('common.delete')">
                                <i class="tim-icons icon-trash-simple"></i>
                            </button>
                        </td>
                    </tr>
                    <tr v-if="tokens.length === 0">
                        <td colspan="7" class="at-empty">
                            {{ $t('apiTokens.empty') }}
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- Create modal -->
        <transition name="modal-fade">
            <div v-if="showCreate" class="modal-overlay" @click="showCreate = false">
                <div class="modal-box at-create-box" @click.stop>
                    <div class="at-modal-header">
                        <h3><i class="tim-icons icon-key-25"></i> {{ $t('apiTokens.create.title') }}</h3>
                        <button @click="showCreate = false" class="at-close">&times;</button>
                    </div>
                    <div class="at-modal-body">
                        <div class="at-field">
                            <label>{{ $t('apiTokens.create.nameLabel') }} *</label>
                            <input type="text" v-model="newTokenName" :placeholder="$t('apiTokens.create.namePlaceholder')" class="at-input">
                            <small>{{ $t('apiTokens.create.nameHint') }}</small>
                        </div>
                        <div class="at-modal-actions">
                            <button class="at-btn-cancel" @click="showCreate = false">{{ $t('common.cancel') }}</button>
                            <button class="at-btn-generate" @click="createToken()" :disabled="!newTokenName.trim() || creating">
                                {{ creating ? $t('apiTokens.create.generating') : $t('apiTokens.create.generate') }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </transition>

        <!-- Token created modal -->
        <transition name="modal-fade">
            <div v-if="createdToken" class="modal-overlay" @click.stop>
                <div class="modal-box at-created-box" @click.stop>
                    <div class="at-modal-header at-modal-header-success">
                        <h3><i class="tim-icons icon-check-2"></i> {{ $t('apiTokens.created.title') }}</h3>
                    </div>
                    <div class="at-modal-body">
                        <div class="at-warning">
                            <i class="tim-icons icon-alert-circle-exc"></i>
                            <span>{{ $t('apiTokens.created.warning') }}</span>
                        </div>
                        <div class="at-token-display">
                            <code>{{ createdToken }}</code>
                            <button class="at-btn-copy" @click="copyToken()">
                                <i class="tim-icons icon-single-copy-04"></i>
                                {{ copied ? $t('apiTokens.created.copied') : $t('apiTokens.created.copy') }}
                            </button>
                        </div>
                        <div class="at-usage-hint">
                            <p><strong>{{ $t('apiTokens.created.usage') }}</strong></p>
                            <code class="at-code-block">curl -H "X-API-Key: {{ createdToken }}" {{ baseUrl }}/api/v1/public/invoices?esirId=...</code>
                        </div>
                        <div class="at-modal-actions">
                            <button class="at-btn-generate" @click="createdToken = null; loadTokens()">
                                {{ $t('apiTokens.created.confirm') }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </transition>

        <!-- Toast -->
        <transition name="notif-fade">
            <div v-if="toast.msg" :class="['at-toast', toast.type]" @click="toast.msg = ''">
                <i :class="toast.type === 'success' ? 'tim-icons icon-check-2' : 'tim-icons icon-alert-circle-exc'"></i>
                <span>{{ toast.msg }}</span>
            </div>
        </transition>
    </div>
</template>

<script>
import axios from '@/util/axiosinstance'

export default {
    name: 'FiscalApiTokens',
    data() {
        return {
            tokens: [],
            showCreate: false,
            newTokenName: '',
            creating: false,
            createdToken: null,
            copied: false,
            toast: { msg: '', type: 'success' },
            baseUrl: window.location.origin,
            docsUrl: window.location.origin + '/docs'
        }
    },
    created() {
        this.loadTokens()
    },
    methods: {
        loadTokens() {
            axios.get('/api/v1/tokens').then(res => { this.tokens = res.data })
        },
        createToken() {
            if (!this.newTokenName.trim()) return
            this.creating = true
            axios.post('/api/v1/token', { name: this.newTokenName.trim() }).then(res => {
                this.creating = false
                this.showCreate = false
                this.createdToken = res.data.token
                this.newTokenName = ''
                this.copied = false
            }).catch(err => {
                this.creating = false
                this.showToast(err.response?.data?.error || this.$t('pos.result.error'), 'error')
            })
        },
        revokeToken(t) {
            if (!confirm(this.$t('apiTokens.revoke.confirm', { name: t.name }))) return
            axios.put('/api/v1/token/' + t.id + '/revoke').then(() => {
                this.showToast(this.$t('apiTokens.revoke.success'), 'success')
                this.loadTokens()
            })
        },
        deleteToken(t) {
            if (!confirm(this.$t('apiTokens.deleteConfirm', { name: t.name }))) return
            axios.delete('/api/v1/token/' + t.id).then(() => {
                this.showToast(this.$t('apiTokens.deleted'), 'success')
                this.loadTokens()
            })
        },
        copyToken() {
            navigator.clipboard.writeText(this.createdToken).then(() => {
                this.copied = true
                setTimeout(() => { this.copied = false }, 3000)
            })
        },
        formatDate(d) {
            if (!d) return '-'
            if (Array.isArray(d)) {
                const [y,m,day,h,min,s] = d
                return String(day).padStart(2,'0')+'.'+String(m).padStart(2,'0')+'.'+y+'. '+
                    String(h||0).padStart(2,'0')+':'+String(min||0).padStart(2,'0')
            }
            const dt = new Date(d)
            return isNaN(dt.getTime()) ? String(d) : dt.toLocaleString('sr-RS')
        },
        showToast(msg, type) {
            if (this._tt) clearTimeout(this._tt)
            this.toast = { msg, type }
            this._tt = setTimeout(() => { this.toast = { msg: '', type: 'success' } }, 4000)
        }
    }
}
</script>

<style scoped>
.at-page { padding: 15px; }
.at-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 15px; }
.at-header h2 { margin: 0; font-size: 20px; color: #2c3e50; }
.at-subtitle { margin: 4px 0 0; font-size: 13px; color: #6c757d; }
.at-btn-new {
    padding: 10px 20px; border: none; background: #1a73e8; color: #fff;
    border-radius: 8px; font-size: 13px; font-weight: 700; cursor: pointer;
    display: flex; align-items: center; gap: 6px; transition: all 0.15s;
}
.at-btn-new:hover { background: #1557b0; }
.card { background: #fff; border-radius: 10px; border: 1px solid #e0e0e0; margin-bottom: 15px; }

/* Docs */
.at-docs {
    padding: 12px 15px; display: flex; align-items: center; gap: 8px;
    font-size: 13px; color: #495057; background: #f0f7ff !important;
    border-color: #bee5eb !important;
}
.at-docs i { color: #1a73e8; }
.at-docs a { color: #1a73e8; font-weight: 600; text-decoration: none; }
.at-docs a:hover { text-decoration: underline; }

/* Table */
.at-table-wrap { overflow-x: auto; }
.at-table { width: 100%; border-collapse: collapse; }
.at-table th {
    padding: 10px 12px; font-size: 10px; font-weight: 700; text-transform: uppercase;
    color: #6c757d; background: #f8f9fa; border-bottom: 1px solid #e9ecef; text-align: left;
}
.at-table td { padding: 10px 12px; border-bottom: 1px solid #f1f3f5; font-size: 13px; color: #2c3e50; }
.at-table tbody tr:hover { background: #f8f9fa; }
.at-inactive { opacity: 0.5; }
.at-token-cell code { background: #f1f3f5; padding: 3px 8px; border-radius: 4px; font-size: 12px; color: #6c757d; }
.at-status { padding: 3px 8px; border-radius: 4px; font-size: 11px; font-weight: 700; }
.at-st-active { background: #d4edda; color: #155724; }
.at-st-revoked { background: #f8d7da; color: #721c24; }
.at-actions { white-space: nowrap; }
.at-btn-revoke, .at-btn-delete {
    padding: 5px 8px; border: none; border-radius: 5px; cursor: pointer;
    font-size: 13px; margin-left: 4px; transition: all 0.15s;
}
.at-btn-revoke { background: #fff3cd; color: #856404; }
.at-btn-revoke:hover { background: #856404; color: #fff; }
.at-btn-delete { background: #f8d7da; color: #dc3545; }
.at-btn-delete:hover { background: #dc3545; color: #fff; }
.at-empty { text-align: center; padding: 40px !important; color: #adb5bd; font-size: 14px; }

/* Modals */
.modal-overlay { position: fixed; top:0;left:0;right:0;bottom:0; background: rgba(0,0,0,0.5); z-index: 10000; display: flex; align-items: center; justify-content: center; }
.modal-box { background: #fff; border-radius: 12px; overflow: hidden; width: 90%; box-shadow: 0 10px 40px rgba(0,0,0,0.2); }
.modal-fade-enter-active, .modal-fade-leave-active { transition: all 0.25s ease; }
.modal-fade-enter, .modal-fade-leave-to { opacity: 0; }
.at-create-box { max-width: 450px; }
.at-created-box { max-width: 600px; }
.at-modal-header {
    padding: 15px 20px; background: #2c3e50; display: flex;
    justify-content: space-between; align-items: center;
}
.at-modal-header h3 { margin: 0; color: #fff; font-size: 16px; display: flex; align-items: center; gap: 8px; }
.at-modal-header-success { background: #28a745; }
.at-close { background: none; border: none; color: #fff; font-size: 24px; cursor: pointer; }
.at-modal-body { padding: 20px; }
.at-field { margin-bottom: 15px; }
.at-field label { display: block; font-size: 12px; font-weight: 700; color: #495057; margin-bottom: 4px; }
.at-field small { display: block; margin-top: 4px; font-size: 11px; color: #6c757d; }
.at-input {
    width: 100%; padding: 9px 12px; border: 1px solid #dee2e6;
    border-radius: 6px; font-size: 14px;
}
.at-input:focus { border-color: #1a73e8; outline: none; }
.at-modal-actions { display: flex; gap: 8px; justify-content: flex-end; }
.at-btn-cancel { padding: 9px 18px; border: 1px solid #dee2e6; background: #fff; border-radius: 6px; cursor: pointer; font-size: 13px; }
.at-btn-generate {
    padding: 9px 18px; border: none; background: #1a73e8; color: #fff;
    border-radius: 6px; cursor: pointer; font-size: 13px; font-weight: 600;
}
.at-btn-generate:disabled { opacity: 0.5; cursor: not-allowed; }

/* Created token display */
.at-warning {
    display: flex; align-items: center; gap: 8px; padding: 10px 14px;
    background: #fff3cd; border-radius: 8px; margin-bottom: 15px;
    font-size: 13px; font-weight: 600; color: #856404;
}
.at-warning i { font-size: 18px; }
.at-token-display {
    display: flex; align-items: center; gap: 8px; margin-bottom: 15px;
    background: #1a1a2e; border-radius: 8px; padding: 12px 15px;
}
.at-token-display code {
    flex: 1; color: #00ff88; font-size: 13px; font-family: 'Courier New', monospace;
    word-break: break-all;
}
.at-btn-copy {
    padding: 6px 12px; border: 1px solid #444; background: transparent;
    color: #fff; border-radius: 6px; cursor: pointer; font-size: 12px;
    display: flex; align-items: center; gap: 4px; white-space: nowrap; transition: all 0.15s;
}
.at-btn-copy:hover { background: #333; }
.at-usage-hint { margin-bottom: 15px; }
.at-usage-hint p { margin: 0 0 6px; font-size: 12px; font-weight: 600; color: #495057; }
.at-code-block {
    display: block; background: #f1f3f5; padding: 10px 12px; border-radius: 6px;
    font-size: 11px; word-break: break-all; color: #495057; line-height: 1.5;
}

/* Toast */
.at-toast {
    position: fixed; top: 80px; left: 50%; transform: translateX(-50%);
    padding: 16px 32px; border-radius: 12px; display: flex; align-items: center; gap: 12px;
    font-size: 16px; font-weight: 600; z-index: 100000; cursor: pointer;
    box-shadow: 0 8px 30px rgba(0,0,0,0.25); color: #fff; max-width: 90vw;
}
.at-toast i { font-size: 22px; }
.at-toast.success { background: #28a745; }
.at-toast.error { background: #dc3545; }
.notif-fade-enter-active, .notif-fade-leave-active { transition: all 0.35s ease; }
.notif-fade-enter, .notif-fade-leave-to { opacity: 0; transform: translateX(-50%) translateY(-30px); }
</style>